import { useState, useCallback } from "react";
import { validateLogic } from "../formLogic/evaluateLogic";

// ─── Operator metadata ────────────────────────────────────────────────────────

const OPERATOR_META = {
    equals:               { label: "equals",             types: ["input","textarea","email","url","select","radio","phone","number","date","datetime","yes_no","rating"] },
    not_equals:           { label: "does not equal",     types: ["input","textarea","email","url","select","radio","phone","number","date","datetime","yes_no","rating"] },
    contains:             { label: "contains",           types: ["input","textarea","email","url","phone"] },
    not_contains:         { label: "does not contain",   types: ["input","textarea","email","url","phone"] },
    greater_than:         { label: "greater than",       types: ["number","rating","date","datetime"] },
    less_than:            { label: "less than",          types: ["number","rating","date","datetime"] },
    greater_than_or_equal:{ label: "≥",                  types: ["number","rating","date","datetime"] },
    less_than_or_equal:   { label: "≤",                  types: ["number","rating","date","datetime"] },
    is_empty:             { label: "is empty",           types: ["input","textarea","email","url","select","radio","checkbox","multi_choice","phone","number","date","datetime"] },
    is_not_empty:         { label: "is not empty",       types: ["input","textarea","email","url","select","radio","checkbox","multi_choice","phone","number","date","datetime"] },
    is_one_of:            { label: "is one of",          types: ["select","radio","checkbox","multi_choice","yes_no"] },
    is_not_one_of:        { label: "is not one of",      types: ["select","radio","checkbox","multi_choice","yes_no"] },
    regex_matches:        { label: "matches regex",      types: ["input","textarea","email","url","phone"], tooltip: "Match a regular expression (advanced). Max 200 characters." },
};

const VALUE_FREE_OPERATORS = new Set(["is_empty", "is_not_empty"]);
const CHOICE_OPERATORS     = new Set(["is_one_of", "is_not_one_of"]);
const CHOICE_TYPES         = new Set(["select","radio","checkbox","multi_choice","yes_no"]);

function operatorsForType(fieldType) {
    return Object.entries(OPERATOR_META)
        .filter(([, meta]) => meta.types.includes(fieldType))
        .map(([key, meta]) => ({ key, label: meta.label, tooltip: meta.tooltip }));
}

// ─── Single condition row ─────────────────────────────────────────────────────

function ConditionRow({ condition, allFields, currentFieldIdx, onChange, onRemove }) {
    const priorFields = allFields.slice(0, currentFieldIdx);
    const sourceField = priorFields.find(f => f.fieldId === condition.field_id || f.name === condition.field_id);
    const availableOps = sourceField ? operatorsForType(sourceField.type) : [];
    const needsValue = condition.operator && !VALUE_FREE_OPERATORS.has(condition.operator);
    const isChoice = sourceField && CHOICE_TYPES.has(sourceField.type) && CHOICE_OPERATORS.has(condition.operator);

    const base = "border dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal";

    function regexError() {
        if (condition.operator !== "regex_matches" || !condition.value) return null;
        try { new RegExp(condition.value); return null; }
        catch (e) { return e.message; }
    }
    const rxErr = regexError();

    return (
        <div className="flex flex-wrap items-start gap-2 mb-2">
            {/* Field picker */}
            <select
                className={`${base} flex-1 min-w-[130px]`}
                value={condition.field_id || ""}
                onChange={e => onChange({ ...condition, field_id: e.target.value, operator: "", value: "" })}
            >
                <option value="">— pick field —</option>
                {priorFields.map(f => (
                    <option key={f.fieldId || f.name} value={f.fieldId || f.name}>
                        {f.label || f.name}
                    </option>
                ))}
            </select>

            {/* Operator picker */}
            {condition.field_id && (
                <select
                    className={`${base} flex-1 min-w-[140px]`}
                    value={condition.operator || ""}
                    onChange={e => onChange({ ...condition, operator: e.target.value, value: "" })}
                    title={condition.operator ? OPERATOR_META[condition.operator]?.tooltip : ""}
                >
                    <option value="">— operator —</option>
                    {availableOps.map(op => (
                        <option key={op.key} value={op.key} title={op.tooltip}>{op.label}</option>
                    ))}
                </select>
            )}

            {/* Value input */}
            {needsValue && condition.operator && (
                <div className="flex-1 min-w-[130px]">
                    {isChoice ? (
                        <select
                            className={base + " w-full"}
                            multiple={CHOICE_OPERATORS.has(condition.operator)}
                            value={Array.isArray(condition.value) ? condition.value : condition.value ? [condition.value] : []}
                            onChange={e => {
                                const vals = Array.from(e.target.selectedOptions, o => o.value);
                                onChange({ ...condition, value: CHOICE_OPERATORS.has(condition.operator) ? vals : vals[0] });
                            }}
                        >
                            {(sourceField.options || []).map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    ) : (
                        <>
                            <input
                                type={["number","rating"].includes(sourceField?.type) ? "number" : "text"}
                                className={`${base} w-full ${rxErr ? "border-red-400" : ""}`}
                                placeholder={condition.operator === "regex_matches" ? "e.g. ^[A-Z].*" : "value"}
                                value={condition.value || ""}
                                onChange={e => onChange({ ...condition, value: e.target.value })}
                            />
                            {rxErr && <p className="text-red-500 text-xs mt-0.5">{rxErr}</p>}
                        </>
                    )}
                </div>
            )}

            {/* Remove */}
            <button
                onClick={onRemove}
                className="text-red-400 hover:text-red-600 px-1 py-1 text-lg leading-none flex-shrink-0"
                title="Remove condition"
            >×</button>
        </div>
    );
}

// ─── Condition group (used for both visibility + jump conditions) ─────────────

function ConditionGroup({ conditions, join, allFields, currentFieldIdx, onChange, onJoinChange, label }) {
    const addCondition = () => onChange([...conditions, { field_id: "", operator: "", value: "" }]);
    const updateRow = (i, updated) => { const c = [...conditions]; c[i] = updated; onChange(c); };
    const removeRow = (i) => onChange(conditions.filter((_, idx) => idx !== i));

    return (
        <div>
            {conditions.length > 1 && (
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500">{label}</span>
                    {["AND","OR"].map(opt => (
                        <button
                            key={opt}
                            onClick={() => onJoinChange(opt)}
                            className={`px-2 py-0.5 text-xs rounded border font-semibold transition-colors ${
                                join === opt
                                    ? "bg-indigo-600 text-white border-indigo-600"
                                    : "border-gray-300 dark:border-gray-600 text-gray-500 hover:border-indigo-400"
                            }`}
                        >{opt}</button>
                    ))}
                    <span className="text-sm text-gray-600 dark:text-gray-400">of the following:</span>
                </div>
            )}

            {conditions.map((cond, i) => (
                <ConditionRow
                    key={i}
                    condition={cond}
                    allFields={allFields}
                    currentFieldIdx={currentFieldIdx}
                    onChange={updated => updateRow(i, updated)}
                    onRemove={() => removeRow(i)}
                />
            ))}

            {(conditions.length === 0 || conditions[conditions.length - 1].field_id) && (
                <button
                    onClick={addCondition}
                    className="text-xs text-indigo-500 hover:text-indigo-700 mt-1"
                >+ Add condition</button>
            )}
        </div>
    );
}

// ─── Flow preview (simple CSS-based node graph) ──────────────────────────────

function FlowPreview({ fields, onClose, onJumpToField }) {
    const lines = [];
    fields.forEach((field, idx) => {
        const jumpRules = (field.logicRules || []).filter(r => r.action === "jump_to" && r.target_id);
        jumpRules.forEach(rule => {
            const targetIdx = fields.findIndex(f => f.name === rule.target_id || f.fieldId === rule.target_id);
            if (targetIdx !== -1) {
                lines.push({ from: idx, to: targetIdx, label: rule.when?.length ? "if condition" : "always" });
            }
        });
    });

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-[#1a1d2e] rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-auto p-6"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-base">Flow Preview</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
                </div>
                <div className="relative">
                    {fields.map((field, idx) => {
                        const hasJump = lines.some(l => l.from === idx);
                        const isJumpTarget = lines.some(l => l.to === idx);
                        const hideRule = (field.logicRules || []).find(r => r.action === "hide");
                        const showRule = (field.logicRules || []).find(r => r.action === "show");
                        return (
                            <div key={field.fieldId || idx} className="flex items-start mb-1">
                                <div className="flex flex-col items-center mr-3 flex-shrink-0">
                                    <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${isJumpTarget ? "bg-orange-400" : "bg-indigo-500"}`} />
                                    {idx < fields.length - 1 && <div className="w-0.5 bg-gray-200 dark:bg-gray-600 flex-1 mt-0.5" style={{ minHeight: 20 }} />}
                                </div>
                                <div
                                    className="flex-1 border dark:border-gray-600 rounded p-2 mb-2 cursor-pointer hover:border-indigo-400 transition-colors"
                                    onClick={() => { onJumpToField(idx); onClose(); }}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <div>
                                            <span className="text-sm text-gray-600 dark:text-gray-400 mr-1">Q{idx + 1}</span>
                                            <span className="text-sm font-medium">{field.label || field.name}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1 justify-end">
                                            {showRule && <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">conditional show</span>}
                                            {hideRule && <span className="text-xs px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded">conditional hide</span>}
                                            {hasJump && <span className="text-xs px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded">→ jumps</span>}
                                        </div>
                                    </div>
                                    {lines.filter(l => l.from === idx).map((line, li) => (
                                        <div key={li} className="text-xs text-orange-500 mt-1">
                                            → jumps to Q{line.to + 1} ({fields[line.to]?.label || fields[line.to]?.name}) [{line.label}]
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Click any question to jump to it in the editor.</p>
            </div>
        </div>
    );
}

// ─── Main LogicBuilder panel ──────────────────────────────────────────────────

/**
 * LogicBuilder — side panel for a single question's logic rules.
 *
 * Props:
 *   field          — the current field object
 *   fieldIdx       — index of the field in form.fields
 *   allFields      — full form.fields array
 *   onUpdate       — (updatedField) => void — called on every change
 *   onClose        — () => void
 *   onShowPreview  — () => void
 */
export default function LogicBuilder({ field, fieldIdx, allFields, onUpdate, onClose, onShowPreview }) {
    // Parse existing rules into UI state
    const initVisibilityRules = () => {
        const rule = (field.logicRules || []).find(r => r.action === "show" || r.action === "hide");
        return {
            enabled: !!rule,
            action: rule?.action || "show",
            conditions: rule?.when?.length ? rule.when : [{ field_id: "", operator: "", value: "" }],
            join: rule?.operator_join || "AND",
        };
    };

    const initJumpRule = () => {
        const rule = (field.logicRules || []).find(r => r.action === "jump_to");
        return {
            enabled: !!rule,
            target_id: rule?.target_id || "",
            useCondition: !!(rule?.when?.length),
            conditions: rule?.when?.length ? rule.when : [{ field_id: "", operator: "", value: "" }],
            join: rule?.operator_join || "AND",
        };
    };

    const [vis, setVis] = useState(initVisibilityRules);
    const [jump, setJump] = useState(initJumpRule);
    const [warnings, setWarnings] = useState([]);

    // Rebuild field's logicRules from UI state and call onUpdate
    const commit = useCallback((nextVis, nextJump) => {
        const rules = [];

        if (nextVis.enabled) {
            const validConds = nextVis.conditions.filter(c => c.field_id && c.operator);
            if (validConds.length) {
                rules.push({
                    id: `vis_${field.fieldId}`,
                    when: validConds,
                    operator_join: nextVis.join,
                    action: nextVis.action,
                    target_id: null,
                });
            }
        }

        if (nextJump.enabled && nextJump.target_id) {
            const rule = {
                id: `jump_${field.fieldId}`,
                when: nextJump.useCondition ? nextJump.conditions.filter(c => c.field_id && c.operator) : [],
                operator_join: nextJump.join,
                action: "jump_to",
                target_id: nextJump.target_id,
            };
            rules.push(rule);
        }

        const updatedField = { ...field, logicRules: rules };

        // Validate the full field set with updated rules
        const allUpdated = allFields.map((f, i) => i === fieldIdx ? updatedField : f);
        try {
            const issues = validateLogic(allUpdated);
            const msgs = issues.map(issue => {
                switch (issue.type) {
                    case "unknown_field_reference": return `Unknown field referenced: "${issue.reference}"`;
                    case "forward_reference": return `Visibility rule references a later field ("${issue.reference}") — only prior fields allowed`;
                    case "invalid_jump_target": return `Jump target "${issue.target}" does not exist`;
                    case "self_jump": return "A field cannot jump to itself";
                    case "jump_cycle": return `Jump cycle detected: ${issue.cycle?.join(" → ")}`;
                    case "unreachable_question": return `Unreachable question: "${issue.label || issue.field}" — no path can reach it (an earlier jump always skips past it)`;
                    case "contradictory_conditions": return `Contradictory rule: conditions on "${issue.reference}" can never all be true — ${issue.reason}`;
                    default: return JSON.stringify(issue);
                }
            });
            setWarnings(msgs);
        } catch (e) {
            setWarnings([e.message]);
        }

        onUpdate(updatedField);
    }, [field, fieldIdx, allFields, onUpdate]);

    const updateVis = (next) => { setVis(next); commit(next, jump); };
    const updateJump = (next) => { setJump(next); commit(vis, next); };

    const priorFields = allFields.slice(0, fieldIdx);
    const laterFields = allFields.slice(fieldIdx + 1);

    const base = "border dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-[#2c2f44] dark:text-[#99a0ac] font-normal";

    return (
        <div
            className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-[#1a1d2e] shadow-2xl z-40 flex flex-col border-l dark:border-gray-700"
            style={{ animation: "slideInRight 0.2s ease-out" }}
        >
            <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

            {/* Header */}
            <div className="flex justify-between items-center px-4 py-3 border-b dark:border-gray-700 flex-shrink-0">
                <div>
                    <h3 className="font-semibold text-sm">Logic</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[240px]">{field.label || field.name}</p>
                </div>
                <div className="flex gap-2 items-center">
                    <button
                        onClick={onShowPreview}
                        className="text-xs text-indigo-500 hover:text-indigo-700 border border-indigo-300 dark:border-indigo-700 rounded px-2 py-1"
                    >Preview flow</button>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">

                {/* Validation status — warnings, or a positive "all good" confirmation */}
                {warnings.length > 0 ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded p-3 space-y-1">
                        <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-300">
                            {warnings.length} issue{warnings.length === 1 ? "" : "s"} found
                        </p>
                        {warnings.map((w, i) => (
                            <p key={i} className="text-xs text-yellow-700 dark:text-yellow-400">{w}</p>
                        ))}
                    </div>
                ) : (vis.length > 0 || jump.length > 0) && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded p-3 flex items-center gap-2">
                        <span className="text-green-600 dark:text-green-400 text-sm leading-none">✓</span>
                        <p className="text-xs font-medium text-green-700 dark:text-green-400">
                            Logic validated — no unreachable questions, loops, or contradictions
                        </p>
                    </div>
                )}

                {/* Visibility section */}
                <section>
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h4 className="text-sm font-semibold">Visibility</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Control when this question appears</p>
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <div
                                onClick={() => updateVis({ ...vis, enabled: !vis.enabled })}
                                className={`w-9 h-5 rounded-full transition-colors relative ${vis.enabled ? "bg-indigo-500" : "bg-gray-300 dark:bg-gray-600"}`}
                            >
                                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${vis.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                            </div>
                        </label>
                    </div>

                    {vis.enabled && (
                        <div className="bg-gray-50 dark:bg-[#23263a] rounded-lg p-3 space-y-3">
                            {/* Action toggle */}
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                    {vis.action === "show" ? "Show" : "Hide"} this question when:
                                </span>
                                <select
                                    className={`${base} text-xs`}
                                    value={vis.action}
                                    onChange={e => updateVis({ ...vis, action: e.target.value })}
                                >
                                    <option value="show">Show</option>
                                    <option value="hide">Hide</option>
                                </select>
                            </div>

                            {priorFields.length === 0 ? (
                                <p className="text-sm text-gray-600 dark:text-gray-400 italic">No prior questions to reference. Add questions above this one first.</p>
                            ) : (
                                <ConditionGroup
                                    conditions={vis.conditions}
                                    join={vis.join}
                                    allFields={allFields}
                                    currentFieldIdx={fieldIdx}
                                    label="Match"
                                    onChange={conditions => updateVis({ ...vis, conditions })}
                                    onJoinChange={join => updateVis({ ...vis, join })}
                                />
                            )}
                        </div>
                    )}
                </section>

                {/* Jump section */}
                <section>
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h4 className="text-sm font-semibold">Jump to</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Skip to a specific question after this one</p>
                        </div>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <div
                                onClick={() => updateJump({ ...jump, enabled: !jump.enabled })}
                                className={`w-9 h-5 rounded-full transition-colors relative ${jump.enabled ? "bg-indigo-500" : "bg-gray-300 dark:bg-gray-600"}`}
                            >
                                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${jump.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                            </div>
                        </label>
                    </div>

                    {jump.enabled && (
                        <div className="bg-gray-50 dark:bg-[#23263a] rounded-lg p-3 space-y-3">
                            {laterFields.length === 0 ? (
                                <p className="text-sm text-gray-600 dark:text-gray-400 italic">No later questions to jump to.</p>
                            ) : (
                                <>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500 flex-shrink-0">Jump to:</span>
                                        <select
                                            className={`${base} flex-1`}
                                            value={jump.target_id}
                                            onChange={e => updateJump({ ...jump, target_id: e.target.value })}
                                        >
                                            <option value="">— select question —</option>
                                            {laterFields.map(f => (
                                                <option key={f.fieldId || f.name} value={f.name || f.fieldId}>
                                                    {f.label || f.name}
                                                </option>
                                            ))}
                                            <option value="__end__">End of form</option>
                                        </select>
                                    </div>

                                    <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={jump.useCondition}
                                            onChange={e => updateJump({ ...jump, useCondition: e.target.checked })}
                                        />
                                        Only jump if condition is met
                                    </label>

                                    {jump.useCondition && (
                                        <ConditionGroup
                                            conditions={jump.conditions}
                                            join={jump.join}
                                            allFields={allFields}
                                            currentFieldIdx={fieldIdx}
                                            label="Jump only if"
                                            onChange={conditions => updateJump({ ...jump, conditions })}
                                            onJoinChange={joinVal => updateJump({ ...jump, join: joinVal })}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </section>

                {/* Current rules summary */}
                {field.logicRules?.length > 0 && (
                    <section>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Active Rules</h4>
                        <div className="space-y-1">
                            {field.logicRules.map((rule, i) => (
                                <div key={i} className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#23263a] rounded px-2 py-1.5">
                                    {rule.action === "show" && "Shows when conditions are met"}
                                    {rule.action === "hide" && "Hides when conditions are met"}
                                    {rule.action === "jump_to" && `Jumps to: ${rule.target_id}${rule.when?.length ? " (conditional)" : " (always)"}`}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <div className="pb-4" />
            </div>
        </div>
    );
}

export { FlowPreview };
